/**
 * Different times on different days of a class.
 *
 * A class has one start and end time, and `day_times` holds a time for any
 * weekday that differs, e.g. `{ saturday: { start_time: "09:00",
 * end_time: "10:30" } }`. The backend reads it the same way: a day without
 * an entry uses the class's own time.
 */

const WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Date.getDay() numbering: Sunday is 0.
const BY_JS_DAY = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/** The weekdays a list of "YYYY-MM-DD" dates fall on, once each, Monday first. */
export const weekdaysInDates = (dates) => {
  const found = new Set();
  for (const raw of dates || []) {
    const [y, m, d] = String(raw).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) continue;
    // Built from its parts, so it is that calendar day wherever the browser is.
    found.add(BY_JS_DAY[new Date(y, m - 1, d).getDay()]);
  }
  return WEEK.filter((day) => found.has(day));
};

/** The form's copy of what the server holds. */
export const dayTimesToForm = (saved) => ({ ...(saved || {}) });

/**
 * What to send. Always a value, never omitted: null is how turning the
 * option off, or every day matching again, clears it on the class.
 */
export const dayTimesToPayload = (enabled, form, weekdays, start, end) => {
  if (!enabled) return null;
  const payload = {};
  for (const day of weekdays) {
    const own = form?.[day];
    if (!own?.start_time || !own?.end_time) continue;
    if (own.start_time === start && own.end_time === end) continue;
    payload[day] = { start_time: own.start_time, end_time: own.end_time };
  }
  return Object.keys(payload).length ? payload : null;
};

export const dayTimeErrors = (enabled, form, weekdays) => {
  const errors = {};
  if (!enabled) return errors;
  for (const day of weekdays) {
    const own = form?.[day];
    if (own?.start_time && own?.end_time && own.start_time >= own.end_time) {
      errors[day] = "End time must be after start time";
    }
  }
  return errors;
};

/**
 * The server's "Monday 5:00 PM - 6:00 PM, Saturday 9:00 AM - 10:30 AM" for
 * a class whose days differ, or null so a view keeps its own format.
 */
export const classHoursLabel = (cls) =>
  Object.keys(cls?.day_times || {}).length && cls?.schedule_label
    ? cls.schedule_label
    : null;
